import React, { useEffect, useState, createElement } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, Users, Building2, Settings, Menu, X, UserPlus, Building, ChevronDown, ChevronUp, UserCircle, Store, UsersRound, TrendingUp, Gift, Receipt, BarChart3, LineChart, Sparkles, Plus, Eye, Shield, Calendar, BookOpen } from 'lucide-react';
interface SidebarProps {
  onAddUser?: () => void;
  onAddPartner?: () => void;
  onCreatePoll?: () => void;
  onCreateCourse?: () => void;
}
const Sidebar: React.FC<SidebarProps> = ({
  onAddUser,
  onAddPartner,
  onCreatePoll,
  onCreateCourse
}) => {
  const [collapsed, setCollapsed] = useState(true);
  const [isMobile, setIsMobile] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const [forceExpanded, setForceExpanded] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState('CRM');
  const location = useLocation();
  const navigate = useNavigate();
  const sectionRoutes = {
    CRM: ['/dashboard', '/users', '/partners', '/settings'],
    'Premi e offerte': ['/marketplace-overview', '/rewards', '/claims'],
    'LMS & Community': ['/lms/overview', '/lms/moderazione', '/lms/programmazione', '/lms/corsi'],
    Statistiche: ['/platform-analytics', '/impact-indices']
  };
  const sectionDefaultRoutes = {
    CRM: '/dashboard',
    'Premi e offerte': '/marketplace-overview',
    'LMS & Community': '/lms/overview',
    Statistiche: '/platform-analytics'
  };
  useEffect(() => {
    const currentPath = location.pathname;
    for (const [section, routes] of Object.entries(sectionRoutes)) {
      if (routes.some(route => currentPath.startsWith(route))) {
        setSelectedSection(section);
        break;
      }
    }
  }, [location.pathname]);
  useEffect(() => {
    const checkIfMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };
    checkIfMobile();
    window.addEventListener('resize', checkIfMobile);
    return () => {
      window.removeEventListener('resize', checkIfMobile);
    };
  }, []);
  const toggleSidebar = () => {
    setCollapsed(!collapsed);
  };
  const handleMouseEnter = () => {
    if (!isMobile) {
      setIsHovering(true);
    }
  };
  const handleMouseLeave = () => {
    if (!isMobile) {
      setIsHovering(false);
    }
  };
  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };
  const handleSectionSelect = (section: string) => {
    if (section === 'Community') {
      return;
    }
    setSelectedSection(section);
    setDropdownOpen(false);
    setForceExpanded(true);
    setTimeout(() => setForceExpanded(false), 3000);
    const defaultRoute = sectionDefaultRoutes[section];
    if (defaultRoute) {
      navigate(defaultRoute);
    }
  };
  const dropdownSections = [{
    icon: UserCircle,
    label: 'CRM',
    value: 'CRM',
    disabled: false
  }, {
    icon: Store,
    label: 'Premi e offerte',
    value: 'Premi e offerte',
    disabled: false
  }, {
    icon: UsersRound,
    label: 'LMS & Community',
    value: 'LMS & Community',
    disabled: false
  }, {
    icon: TrendingUp,
    label: 'Statistiche',
    value: 'Statistiche',
    disabled: false
  }];
  const sectionMenuItems = {
    CRM: [{
      icon: LayoutDashboard,
      label: 'Dashboard',
      path: '/dashboard',
      active: location.pathname === '/dashboard' || location.pathname === '/'
    }, {
      icon: Users,
      label: 'Utenti',
      path: '/users',
      active: location.pathname.startsWith('/users')
    }, {
      icon: Building2,
      label: 'Partners',
      path: '/partners',
      active: location.pathname.startsWith('/partners')
    }, {
      icon: Settings,
      label: 'Impostazioni',
      path: '/settings',
      active: location.pathname === '/settings'
    }],
    'Premi e offerte': [{
      icon: Eye,
      label: 'Panoramica',
      path: '/marketplace-overview',
      active: location.pathname === '/marketplace-overview'
    }, {
      icon: Gift,
      label: 'Premi',
      path: '/rewards',
      active: location.pathname.startsWith('/rewards')
    }, {
      icon: Receipt,
      label: 'Riscatti',
      path: '/claims',
      active: location.pathname.startsWith('/claims')
    }],
    'LMS & Community': [{
      icon: Eye,
      label: 'Panoramica',
      path: '/lms/overview',
      active: location.pathname === '/lms/overview'
    }, {
      icon: Shield,
      label: 'Moderazione',
      path: '/lms/moderazione',
      active: location.pathname === '/lms/moderazione'
    }, {
      icon: Calendar,
      label: 'Programmazione',
      path: '/lms/programmazione',
      active: location.pathname === '/lms/programmazione'
    }, {
      icon: BookOpen,
      label: 'Corsi',
      path: '/lms/corsi',
      active: location.pathname === '/lms/corsi'
    }],
    Statistiche: [{
      icon: LineChart,
      label: 'Analitiche Piattaforma',
      path: '/platform-analytics',
      active: location.pathname === '/platform-analytics'
    }, {
      icon: BarChart3,
      label: 'Indici di Impatto',
      path: '/impact-indices',
      active: location.pathname === '/impact-indices'
    }]
  };
  const sectionQuickActions = {
    CRM: [{
      icon: UserPlus,
      label: 'Aggiungi Utente',
      onClick: () => {
        if (location.pathname.startsWith('/users')) {
          onAddUser?.();
        } else {
          navigate('/users', {
            state: {
              openDrawer: true
            }
          });
        }
      }
    }, {
      icon: Building,
      label: 'Aggiungi Partner',
      onClick: () => {
        if (location.pathname.startsWith('/partners')) {
          onAddPartner?.();
        } else {
          navigate('/partners', {
            state: {
              openDrawer: true
            }
          });
        }
      }
    }],
    'Premi e offerte': [{
      icon: Plus,
      label: 'Crea Premio',
      onClick: () => navigate('/rewards/new')
    }],
    'LMS & Community': [{
      icon: Plus,
      label: 'Crea Sondaggio',
      onClick: () => {
        if (location.pathname === '/lms/programmazione') {
          onCreatePoll?.();
        } else {
          navigate('/lms/programmazione', {
            state: {
              openDrawer: true,
              type: 'poll'
            }
          });
        }
      }
    }, {
      icon: Plus,
      label: 'Crea Corso',
      onClick: () => {
        if (location.pathname === '/lms/corsi') {
          onCreateCourse?.();
        } else {
          navigate('/lms/corsi', {
            state: {
              openDrawer: true
            }
          });
        }
      }
    }],
    Statistiche: []
  };
  const currentMenuItems = sectionMenuItems[selectedSection] || [];
  const currentQuickActions = sectionQuickActions[selectedSection] || [];
  const selectedSectionIcon = dropdownSections.find(s => s.value === selectedSection)?.icon || UserCircle;
  const isExpanded = isMobile ? !collapsed : isHovering || forceExpanded;
  return <aside className={`text-white flex flex-col fixed h-full z-40 transition-all duration-150 ${isExpanded ? 'w-64' : 'w-20'} lg:relative bg-greenback`} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave} style={{
    position: 'absolute'
  }} data-id="element-1099">
      <button className="lg:hidden absolute right-4 top-4 text-white" onClick={toggleSidebar} data-id="element-1100">
        {collapsed ? <Menu size={20} data-id="element-1101" /> : <X size={20} data-id="element-1102" />}
      </button>

      {/* Logo Area - Fixed Height */}
      <div className="flex items-center h-[88px] border-b border-white/20 overflow-hidden" data-id="element-1103">
        <div className="w-20 flex justify-center flex-shrink-0" data-id="element-1104">
          <img src="https://cdn.magicpatterns.com/uploads/w4Sqqi6gRHWGegGAQT2PTG/Greenback_logo_(1).png" alt="Greenback logo" className="h-10 w-10 brightness-0 invert transition-all duration-150" data-id="element-1105" />
        </div>
        <div className={`transition-all duration-150 ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 overflow-hidden'}`} data-id="element-1106">
          <h2 className="text-xl font-normal text-white whitespace-nowrap" data-id="element-1107">
            Greenback
          </h2>
          <p className="text-sm font-normal text-white whitespace-nowrap" data-id="element-1108">
            Admin Dashboard
          </p>
        </div>
      </div>

      {/* Main Navigation */}
      <nav className="py-6 px-3" data-id="element-1109">
        <ul className="space-y-1" data-id="element-1110">
          {/* Section Selector Dropdown - rounded-full + outline + increased spacing */}
          <li className="relative mb-3" data-id="element-1111">
            <button onClick={toggleDropdown} className={`w-full flex items-center py-3 px-3 rounded-full outline outline-2 outline-white/20 transition-colors ${dropdownOpen ? 'bg-white/10 text-white' : 'text-white bg-white/10 hover:bg-white/30'}`} data-id="element-1112">
              <div className="w-8 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-1113">
                {createElement(selectedSectionIcon, {
                className: 'h-5 w-5'
              })}
              </div>
              <span className={`font-medium whitespace-nowrap transition-all duration-150 flex-1 text-left ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 overflow-hidden'}`} data-id="element-1114">
                {selectedSection}
              </span>
              <div className={`transition-all duration-150 ${isExpanded ? 'opacity-100' : 'opacity-0'}`} data-id="element-1115">
                {dropdownOpen ? <ChevronUp className="h-4 w-4" data-id="element-1116" /> : <ChevronDown className="h-4 w-4" data-id="element-1117" />}
              </div>
            </button>

            {/* Dropdown Menu - Changed to rounded-lg for more rounded corners */}
            {dropdownOpen && isExpanded && <ul className="absolute left-0 right-0 mt-1 space-y-1 bg-greenback rounded-lg p-2 z-50 border border-white/20 shadow-lg" data-id="element-1118">
                {dropdownSections.map((section, index) => <li key={index} data-id="element-1119">
                    <button onClick={() => handleSectionSelect(section.value)} disabled={section.disabled} className={`w-full flex items-center py-2 px-3 rounded-lg transition-colors ${selectedSection === section.value ? 'bg-white/20 text-white' : section.disabled ? 'text-white/40 cursor-not-allowed' : 'text-white/80 hover:bg-white/10 hover:text-white'}`} data-id="element-1120">
                      <section.icon className="h-4 w-4 mr-3 flex-shrink-0" data-id="element-1121" />
                      <span className="font-medium whitespace-nowrap" data-id="element-1122">
                        {section.label}
                      </span>
                    </button>
                  </li>)}
              </ul>}
          </li>

          {/* Dynamic Menu Items - rounded-full */}
          {currentMenuItems.map((item, index) => <li key={index} data-id="element-1123">
              <Link to={item.path} className={`flex items-center py-3 px-3 rounded-full transition-colors ${item.active ? 'bg-white text-greenback' : 'text-white hover:bg-white/10'}`} data-id="element-1124">
                <div className="w-8 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-1125">
                  <item.icon className="h-5 w-5" data-id="element-1126" />
                </div>
                <span className={`font-medium whitespace-nowrap transition-all duration-150 ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 overflow-hidden'}`} data-id="element-1127">
                  {item.label}
                </span>
              </Link>
            </li>)}
        </ul>
      </nav>

      {/* Quick Actions - rounded-full */}
      {currentQuickActions.length > 0 && <div className="border-t border-white/20 overflow-hidden px-3" data-id="element-1128">
          <div className={`h-[32px] flex items-center transition-all duration-150 ${isExpanded ? 'opacity-100' : 'opacity-0'}`} data-id="element-1129">
            <p className="text-xs font-semibold text-white/60 uppercase tracking-wider whitespace-nowrap" data-id="element-1130">
              Azioni Rapide
            </p>
          </div>
          <ul className="space-y-1" data-id="element-1131">
            {currentQuickActions.map((action, index) => <li key={index} data-id="element-1132">
                <button onClick={action.onClick} className="w-full flex items-center h-[52px] px-3 rounded-full transition-colors text-white hover:bg-white/10" data-id="element-1133">
                  <div className="w-8 flex items-center justify-center flex-shrink-0 mr-3" data-id="element-1134">
                    <action.icon className="h-5 w-5" data-id="element-1135" />
                  </div>
                  <span className={`font-medium whitespace-nowrap transition-all duration-150 ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 overflow-hidden'}`} data-id="element-1136">
                    {action.label}
                  </span>
                </button>
              </li>)}
          </ul>
        </div>}

      {/* Footer Version - Fixed Height */}
      <div className="mt-auto border-t border-white/20 px-3" data-id="element-1137">
        <div className="flex items-center h-[72px] overflow-hidden" data-id="element-1138">
          <div className="w-8 flex justify-center flex-shrink-0 mr-3" data-id="element-1139">
            <span className="text-xs font-normal text-white" data-id="element-1140">v1.0</span>
          </div>
          <div className={`text-xs font-normal text-white transition-all duration-150 ${isExpanded ? 'opacity-100 max-w-full' : 'opacity-0 max-w-0 overflow-hidden'}`} data-id="element-1141">
            <p className="whitespace-nowrap" data-id="element-1142">© 2025 Greenback</p>
            <p className="whitespace-nowrap" data-id="element-1143">Versione 1.0.0</p>
          </div>
        </div>
      </div>
    </aside>;
};
export default Sidebar;